/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {NetworkApiService} from '../network/network-api.service';
import {isTierEnabled} from './tier-commons';
import {TierApiService, TierModel} from './tier-api.service';
import {TierEditPageComponent} from './tier-edit-page.component';
import {TierRoutePageComponent} from './tier-route-page.component';

export function tierStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(TierApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const localNetworkProfile = adnRouterHelper.services.localNetworkProfile();
  const Network = adnRouterHelper.services.service(NetworkApiService);
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetworkPermission('POLITICAL_ADMIN', null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper('tiers.tier', TierApiService, 'app.adm', 'app/admin');

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: TierRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      tierPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<TierModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true,
            readOnly(),
            api,
            'Tier'
          );
        }
        return Promise.all([
          firstValueFrom(api.get($stateParams.id).pipe(
            tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
          )),
          firstValueFrom(Network.get(localNetworkProfile.getNetworkId()))
        ]).then(([model, network]) => new SignalAdminResourcePageState(
          model,
          false,
          readOnly(),
          api,
          'Tier',
          'name',
          undefined,
          isTierEnabled(model.id, network)
            ? 'Disable this tier before changing its object state.'
            : undefined
        ));
      },
      model: function(tierPage: SignalAdminResourcePageState<TierModel>) {
        return tierPage.model();
      }
    },
    views: {
      'content@app': {component: TierEditPageComponent, bindings: {state: 'tierPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'tierPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'tierPage'}}
    }
  }));
  return states.build();
}
